/**
 * Division selection uses two stores:
 * - sessionStorage: active division for this tab/window (tabs can differ)
 * - localStorage: last division the user chose (seeds new tabs)
 */
export const SELECTED_DIVISION_STORAGE_KEY = 'selectedDivisionId';

const readRaw = (storage: Storage): string | null =>
  storage.getItem(SELECTED_DIVISION_STORAGE_KEY);

const parseValidDivisionRaw = (raw: string | null): string | null => {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 1 ? raw : null;
};

/** This tab's division, or the last globally selected division when opening a new tab. */
export const getSelectedDivisionIdRaw = (): string | null => {
  const session = parseValidDivisionRaw(readRaw(sessionStorage));
  if (session) return session;

  const last = parseValidDivisionRaw(readRaw(localStorage));
  if (last) {
    sessionStorage.setItem(SELECTED_DIVISION_STORAGE_KEY, last);
  }
  return last;
};

export const getSelectedDivisionId = (): number => {
  const raw = getSelectedDivisionIdRaw();
  const parsed = raw ? Number(raw) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getSelectedDivisionIdOrUndefined = (): number | undefined => {
  const id = getSelectedDivisionId();
  return id >= 1 ? id : undefined;
};

export const setSelectedDivisionId = (id: string | number): void => {
  const value = String(id);
  sessionStorage.setItem(SELECTED_DIVISION_STORAGE_KEY, value);
  localStorage.setItem(SELECTED_DIVISION_STORAGE_KEY, value);
};

/** Notify listeners that the user changed division (not for initial header sync). */
export const notifySelectedDivisionChanged = (): void => {
  window.dispatchEvent(new Event('selectedDivisionChanged'));
};

export const removeSelectedDivisionId = (): void => {
  sessionStorage.removeItem(SELECTED_DIVISION_STORAGE_KEY);
  localStorage.removeItem(SELECTED_DIVISION_STORAGE_KEY);
};
